"use client";

import React from "react";
import Image from "next/image";

interface TransformationStep {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

interface TransformationStepsProps {
  title?: string;
  subtitle?: string;
  steps?: TransformationStep[];
}

const defaultSteps: TransformationStep[] = [
  {
    id: "tu-van-xanh",
    title: "Tư vấn xanh",
    description:
      "Đây là bước đầu tiên trong quy trình chuyển đổi xanh, tập trung vào việc phân tích và đánh giá hiện trạng của doanh nghiệp hoặc đô thị để đưa ra các giải pháp tối ưu để giảm thiểu tác động môi trường. Các chuyên gia tư vấn sẽ xác định các nguồn gây ô nhiễm và đề xuất các biện pháp sử dụng tài nguyên một cách hiệu quả, giảm phát thải và áp dụng công nghệ xanh.",
    image: "/tuvanxanh.png", // Placeholder - replace with actual image
    imageAlt: "Tư vấn xanh - Green consulting",
  },
  {
    id: "thiet-ke-xanh",
    title: "Thiết kế xanh",
    description:
      "Sau khi tư vấn, bước tiếp theo là thiết kế các giải pháp bền vững. Thiết kế xanh không chỉ xem xét về mặt thẩm mỹ mà còn cảng, na góc chủ trọng nhấn mạnh việc tối ưu hóa hiệu quả năng lượng, tận dụng ánh sáng và gió tự nhiên, và giảm thiểu vật liệu gây hại cho môi trường. Các dự án thiết kế xanh thường sử dụng vật liệu tái chế hoặc có khả năng phân hủy sinh học.",
    image: "/thietkexanh.jpeg", // Placeholder - replace with actual image
    imageAlt: "Thiết kế xanh - Green design",
  },
  {
    id: "thi-cong-xanh",
    title: "Thi công xanh",
    description:
      "Bước ba bao gồm việc triển khai các giải pháp thiết kế xanh trong thực tế, đảm bảo rằng các công đoạn xây dựng được thực hiện một cách thân thiện với môi trường. Thi công xanh yêu cầu sử dụng máy móc, thiết bị và công nghệ tiết kiệm năng lượng, cũng như giảm thiểu phát thải và rác thải xung quanh thi công xanh nhiên xung quanh.",
    image: "/thicongxanh.webp", // Placeholder - replace with actual image
    imageAlt: "Thi công xanh - Green construction",
  },
  {
    id: "van-hanh-xanh",
    title: "Vận hành xanh",
    description:
      "Sau khi dự án hoàn thành, việc vận hành xanh sẽ giúp duy trì bền vững lâu dài của công trình. Vận hành xanh bao gồm việc quản lý, giảm sát và duy trì các tiêu chuẩn về năng lượng và môi trường, như tối ưu hóa hệ thống chiếu sáng, điều hòa, và quản lý chất thải. Bước này cũng bao gồm việc nâng cao nhận thức của người sử dụng về bảo vệ môi trường. Quy trình chuyển đổi xanh không chỉ giúp giảm thiểu tác động tiêu cực lên môi trường mà còn tiết kiệm chi phí lâu dài, tạo ra các giá trị bền vững cho doanh nghiệp và cộng đồng.",
    image: "/vanhanhxanh.webp", // Placeholder - replace with actual image
    imageAlt: "Vận hành xanh - Green operation",
  },
];

/**
 * TransformationSteps Component
 * Displays the 4-step green transformation process
 */
const TransformationSteps: React.FC<TransformationStepsProps> = ({
  title = "Chuyển đổi xanh: Các Bước Hướng Tới Phát Triển Bền Vững",
  subtitle = "Chuyển đổi xanh là quá trình đưa ra các giải pháp giúp doanh nghiệp, tổ chức và cá nhân hướng tới phát triển bền vững, giảm thiểu tác động tiêu cực đến môi trường. Quy trình chuyển đổi xanh có thể được chia thành bốn bước chính: tư vấn xanh, thiết kế xanh, thi công xanh và vận hành xanh.",
  steps = defaultSteps,
}) => {
  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            {title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-4xl">
            {subtitle}
          </p>
        </div>

        {/* Transformation Steps */}
        <div className="space-y-16">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image Section */}
              <div className="flex-1 max-w-md">
                <div className="relative aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <Image
                    src={step.image}
                    alt={step.imageAlt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 space-y-6">
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {step.description}
                  </p>
                </div>

                {/* Step Number Badge */}
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <div className="ml-4 text-sm text-gray-500 font-medium">
                    Bước {index + 1}/4
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TransformationSteps;
